/**
 * Price handler with integrated currency conversion for Bulgaria's Eurozone accession
 * Uses a fixed exchange rate of 1 EUR = 1.95583 BGN (Bulgarian National Bank rate)
 * Time slots are based on Bulgarian timezone (Europe/Sofia) as per Bulgarian law
 */
class PriceHandler {
    constructor() {
        // Fixed exchange rate (1 EUR = 1.95583 BGN)
        this.EXCHANGE_RATE = 1.95583

        // Bulgarian timezone for law compliance
        this.BULGARIAN_TIMEZONE = 'Europe/Sofia'

        // Time slots for dual currency display

        // Slot 1: from 8 Aug 2025 to 31 Dec 2025 - Showing prices in BGN & EUR
        this.SLOT_1_START = new Date('2025-08-08T00:00:00+03:00').getTime()
        this.SLOT_1_END = new Date('2025-12-31T23:59:59+02:00').getTime()

        // Slot 2: from 1 Jan 2026 to 8 Aug 2026 - Showing prices in EUR & BGN
        this.SLOT_2_START = new Date('2026-01-01T00:00:00+02:00').getTime()
        this.SLOT_2_END = new Date('2026-08-08T23:59:59+03:00').getTime()

        // Slot 3: from 9 Aug 2026 onwards - Showing prices only in EUR
        this.SLOT_3_START = new Date('2026-08-09T00:00:00+03:00').getTime()
    }

    /**
     * Get current time in Bulgarian timezone
     * @param {Date|number} [date] - Optional date or timestamp for testing
     * @returns {number} - Timestamp in Bulgarian timezone
     */
    getBulgarianTime(date = Date.now()) {
        const inputDate = typeof date === 'number' ? new Date(date) : date

        // Convert to Bulgarian timezone
        const bulgarianDate = new Date(inputDate.toLocaleString('en-US', { timeZone: this.BULGARIAN_TIMEZONE }))

        return bulgarianDate.getTime()
    }

    /**
     * Get the current slot based on Bulgarian time
     * @param {Date|number} [date] - Optional date or timestamp for testing
     * @returns {1|2|3|4}
     */
    getCurrentSlot(date = Date.now()) {
        const bulgarianTime = this.getBulgarianTime(date)

        if (bulgarianTime < this.SLOT_1_START) return 1
        if (bulgarianTime >= this.SLOT_1_START && bulgarianTime <= this.SLOT_1_END) return 2
        if (bulgarianTime >= this.SLOT_2_START && bulgarianTime < this.SLOT_2_END) return 3
        if (bulgarianTime >= this.SLOT_3_START) return 4
        // fallback
        return 1
    }

    /**
     * Round to 2 decimal places with proper mathematical rounding (round half up)
     * @param {number} value - The value to round
     * @returns {number} - Rounded value
     */
    roundToTwoDecimals(value) {
        // Convert to string to avoid floating point precision issues
        const str = value.toFixed(6)
        const parts = str.split('.')

        if (parts.length === 1) {
            return Number(parts[0])
        }

        const integerPart = parts[0]
        const decimalPart = parts[1]

        // Take first 3 digits of decimal part for rounding decision
        const firstThree = decimalPart.substring(0, 3)
        const thirdDigit = parseInt(firstThree[2] || '0')

        // Round based on third digit
        let roundedDecimal
        if (thirdDigit >= 5) {
            // Round up
            const firstTwo = parseInt(firstThree.substring(0, 2))
            roundedDecimal = (firstTwo + 1).toString().padStart(2, '0')
        } else {
            // Round down
            roundedDecimal = firstThree.substring(0, 2)
        }

        // Handle case where rounding up causes overflow (e.g., 99.995 -> 100.00)
        if (roundedDecimal === '100') {
            return Number(integerPart) + 1
        }

        return Number(integerPart + '.' + roundedDecimal)
    }

    /**
     * Convert amount from one currency to another with high precision
     * @param {Number} amount - The amount to convert
     * @param {String} fromCurrency - Source currency (BGN or EUR)
     * @param {String} toCurrency - Target currency (BGN or EUR)
     * @param {Boolean} [trimZeros=true] - Whether to trim trailing zeros after decimal point
     * @param {Number} [decimalPlaces=2] - Number of decimal places to use for formatting
     * @returns {String} - Converted amount as string
     */
    convertCurrency(amount, fromCurrency, toCurrency, trimZeros = true, decimalPlaces = 2) {
        let result

        if (fromCurrency === toCurrency) {
            result = Number(amount)
        } else if (fromCurrency === 'BGN' && toCurrency === 'EUR') {
            result = Number(amount) / this.EXCHANGE_RATE
        } else if (fromCurrency === 'EUR' && toCurrency === 'BGN') {
            result = Number(amount) * this.EXCHANGE_RATE
        } else {
            console.error(`Unsupported currency conversion: ${fromCurrency} to ${toCurrency}`)
            return 'ERROR'
        }

        // Convert to string with proper decimal formatting
        let resultString

        // Handle very small numbers with appropriate precision only when decimalPlaces >= 3
        if (result < 0.001 && result > 0 && decimalPlaces >= 3) {
            // For very small numbers, show up to 6 decimal places without rounding
            resultString = result.toFixed(6)
        } else if (result < 0.01 && result > 0 && decimalPlaces >= 3) {
            // For small numbers, show up to 5 decimal places without rounding
            resultString = result.toFixed(5)
        } else if (result < 0.1 && result > 0 && decimalPlaces >= 3) {
            // For small numbers, show up to 4 decimal places without rounding
            resultString = result.toFixed(4)
        } else {
            // For normal numbers or when decimalPlaces < 3, use specified decimal places
            resultString = result.toFixed(decimalPlaces)
        }

        // Ensure exactly specified decimal places when trimZeros is false
        if (!trimZeros) {
            // Convert to number and format with exactly specified decimal places
            const numResult = Number(resultString)
            resultString = numResult.toFixed(decimalPlaces)
        } else {
            // Trim trailing zeros if requested, but keep appropriate precision for small numbers
            if (result < 0.001 && result > 0 && decimalPlaces >= 3) {
                // For very small numbers, keep at least 5 decimal places
                resultString = result.toFixed(5).replace(/0+$/, '').replace(/\.$/, '')
            } else if (result < 0.01 && result > 0 && decimalPlaces >= 3) {
                // For small numbers, keep at least 4 decimal places
                resultString = result.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')
            } else if (result < 0.1 && result > 0 && decimalPlaces >= 3) {
                // For small numbers, keep at least 3 decimal places
                resultString = result.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
            } else {
                // For normal numbers, trim trailing zeros but respect decimalPlaces
                resultString = result.toFixed(decimalPlaces).replace(/0+$/, '').replace(/\.$/, '')
            }
        }

        return resultString
    }

    /**
     * Format price with two decimal places
     * @param {Object} priceData - Price data object with whole and decimal parts
     * @returns {string} Formatted price string
     */
    formatPriceWithTwoDecimals(priceData) {
        // Combine whole and decimal parts into a single number
        const fullPrice = parseFloat(priceData.whole) + '.' + parseFloat(priceData.decimal)

        // Format with two decimal places
        return parseFloat(fullPrice).toFixed(2)
    }

    /**
     * Format currency with custom decimal places
     * @param {number} amount - Amount to format
     * @param {string} currency - Currency code
     * @param {string} locale - Locale (bg, en)
     * @param {number} [decimalPlaces=2] - Number of decimal places to show
     * @returns {string} Formatted currency string
     */
    formatCurrency(amount, currency, locale, decimalPlaces = 2) {
        return new Intl.NumberFormat(locale === 'bg' ? 'bg-BG' : 'en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces
        }).format(parseFloat(amount).toFixed(decimalPlaces))
    }

    /**
     * Format price for display with dual currency support and custom decimal places
     * @param {number} price - Price amount in BGN
     * @param {object} options - Formatting options
     * @param {string} options.locale - Locale (bg, en)
     * @param {number} options.decimalPlaces - Number of decimal places to show (default: 2)
     * @returns {object} Formatted price object with dual currency support
     */
    formatPrice(price, options = {}) {
        const { locale = 'bg', decimalPlaces = 2 } = options
        const slot = this.getCurrentSlot()

        // Format price with custom decimal places
        const formattedPrice = Number(price).toFixed(decimalPlaces)
        const parts = formattedPrice.split('.')
        const whole = parts[0]
        const decimal = parts[1]

        // Default single currency display
        let result = {
            showDual: false,
            primary: {
                whole: whole,
                decimal: decimal,
                currency: 'BGN',
                symbol: this.formatCurrency(price, 'BGN', locale, decimalPlaces)
            },
            secondary: null
        }

        if (slot === 1) {
            // Before 1 Jul 2025: show only BGN
            return result
        } else if (slot === 2) {
            // 1 Jul 2025 – 31 Dec 2025: Show BGN / EUR
            const eurAmount = this.convertCurrency(price, 'BGN', 'EUR', true, decimalPlaces)
            const eurPrice = parseFloat(eurAmount)

            result.showDual = true
            result.secondary = {
                whole: eurAmount.split('.')[0],
                decimal: eurAmount.split('.')[1] || '00',
                currency: 'EUR',
                symbol: this.formatCurrency(eurPrice, 'EUR', locale, decimalPlaces)
            }
        } else if (slot === 3) {
            // 1 Jan 2026 – 1 Jul 2026: Show EUR / BGN
            const eurAmount = this.convertCurrency(price, 'BGN', 'EUR', true, decimalPlaces)
            const eurPrice = parseFloat(eurAmount)

            result.showDual = true
            result.primary = {
                whole: eurAmount.split('.')[0],
                decimal: eurAmount.split('.')[1] || '00',
                currency: 'EUR',
                symbol: this.formatCurrency(eurPrice, 'EUR', locale, decimalPlaces)
            }
            result.secondary = {
                whole: whole,
                decimal: decimal,
                currency: 'BGN',
                symbol: this.formatCurrency(price, 'BGN', locale, decimalPlaces)
            }
        } else if (slot === 4) {
            // After 1 Jul 2026: show only EUR
            const eurAmount = this.convertCurrency(price, 'BGN', 'EUR', true, decimalPlaces)
            const eurPrice = parseFloat(eurAmount)

            result.primary = {
                whole: eurAmount.split('.')[0],
                decimal: eurAmount.split('.')[1] || '00',
                currency: 'EUR',
                symbol: this.formatCurrency(eurPrice, 'EUR', locale, decimalPlaces)
            }
        }

        return result
    }

    /**
     * Create dual currency text
     * @param {Object} formattedPrice - Formatted price object from formatPrice()
     * @returns {string} Formatted dual currency text
     */
    createDualCurrencyText(formattedPrice) {
        if (formattedPrice.showDual) {
            return `${formattedPrice.primary.symbol} / ${formattedPrice.secondary.symbol}`
        } else {
            return formattedPrice.primary.symbol
        }
    }

    /**
     * Replace placeholder in text
     * @param {string} text - Original text
     * @param {string} placeholder - Placeholder to replace
     * @param {string} replacement - Replacement text
     * @returns {string} Updated text
     */
    replacePlaceholder(text, placeholder, replacement) {
        return text.replace(placeholder, replacement)
    }

    /**
     * Update element text content
     * @param {Element} element - DOM element to update
     * @param {string} text - New text content
     */
    updateElementText(element, text) {
        element.textContent = text
    }

    /**
     * Update element HTML content
     * @param {Element} element - DOM element to update
     * @param {string} html - New HTML content
     */
    updateElementHTML(element, html) {
        element.innerHTML = html
    }

    /**
     * Show/hide element
     * @param {Element} element - DOM element
     * @param {boolean} show - Whether to show the element
     * @param {string} displayType - Display type (default: 'block')
     */
    toggleElement(element, show, displayType = 'block') {
        if (element) {
            element.style.display = show ? displayType : 'none'
        }
    }

    /**
     * Find elements by selector
     * @param {string} selector - CSS selector
     * @param {Element} parent - Parent element (optional)
     * @returns {NodeList} Found elements
     */
    findElements(selector, parent = document) {
        return parent.querySelectorAll(selector)
    }

    /**
     * Process elements with price data
     * @param {string} selector - CSS selector for elements with data-price
     * @param {Function} processor - Function to process each element
     */
    processPriceElements(selector, processor) {
        const elements = this.findElements(selector)

        elements.forEach((element) => {
            const data = this.getDataAttributes(element)

            if (data.price && !isNaN(data.price)) {
                processor(element, data)
            }
        })
    }

    /**
     * Get meta tag value
     * @param {string} name - Meta tag name
     * @returns {string|null} Meta tag content or null if not found
     */
    getMetaTag(name) {
        const metaTag = document.querySelector(`meta[name="${name}"]`)
        return metaTag ? metaTag.getAttribute('content') : null
    }

    /**
     * Check if dual currency should be displayed based on locale and currency
     * @returns {boolean} Whether to show dual currency
     */
    shouldShowDualCurrency() {
        const locale = this.getMetaTag('locale')
        const currency = this.getMetaTag('currency')

        // Show dual currency for Bulgarian or English locale and BGN currency only
        return (locale === 'bg' || locale === 'en') && currency === 'BGN'
    }

    /**
     * Format price using original prices from data attributes based on time slot
     * @param {number} priceBgn - Price in BGN
     * @param {number} priceEur - Price in EUR
     * @param {object} options - Formatting options
     * @param {string} options.locale - Locale (bg, en)
     * @param {number} options.decimalPlaces - Number of decimal places to show (default: 2)
     * @returns {object} Formatted price object with dual currency support
     */
    formatPriceWithOriginalPrices(priceBgn, priceEur, options = {}) {
        const { locale = 'bg', decimalPlaces = 2 } = options
        const slot = this.getCurrentSlot()

        // Format prices with custom decimal places
        const formattedBgn = Number(priceBgn).toFixed(decimalPlaces)
        const formattedEur = Number(priceEur).toFixed(decimalPlaces)
        const bgnParts = formattedBgn.split('.')
        const eurParts = formattedEur.split('.')

        // Default single currency display
        let result = {
            showDual: false,
            primary: {
                whole: bgnParts[0],
                decimal: bgnParts[1],
                currency: 'BGN',
                symbol: this.formatCurrency(priceBgn, 'BGN', locale, decimalPlaces)
            },
            secondary: null
        }

        if (slot === 1) {
            // Before 8 Aug 2025: show only BGN
            return result
        } else if (slot === 2) {
            // 8 Aug 2025 – 31 Dec 2025: Show BGN / EUR
            result.showDual = true
            result.secondary = {
                whole: eurParts[0],
                decimal: eurParts[1] || '00',
                currency: 'EUR',
                symbol: this.formatCurrency(priceEur, 'EUR', locale, decimalPlaces)
            }
        } else if (slot === 3) {
            // 1 Jan 2026 – 8 Aug 2026: Show EUR / BGN
            result.showDual = true
            result.primary = {
                whole: eurParts[0],
                decimal: eurParts[1] || '00',
                currency: 'EUR',
                symbol: this.formatCurrency(priceEur, 'EUR', locale, decimalPlaces)
            }
            result.secondary = {
                whole: bgnParts[0],
                decimal: bgnParts[1],
                currency: 'BGN',
                symbol: this.formatCurrency(priceBgn, 'BGN', locale, decimalPlaces)
            }
        } else if (slot === 4) {
            // After 8 Aug 2026: show only EUR
            result.primary = {
                whole: eurParts[0],
                decimal: eurParts[1] || '00',
                currency: 'EUR',
                symbol: this.formatCurrency(priceEur, 'EUR', locale, decimalPlaces)
            }
        }

        return result
    }

    /**
     * Update price element using original prices from data attributes based on time slot
     * @param {Element} priceElement - Price element to update
     * @returns {boolean} Whether the element was updated
     */
    updateElementWithOriginalPrices(priceElement) {
        if (!priceElement) return false

        const locale = this.getMetaTag('locale')
        const currency = this.getMetaTag('currency')
        let decimalPlaces = 2

        if (priceElement.hasAttribute('data-decimal-places')) {
            decimalPlaces = parseInt(priceElement.getAttribute('data-decimal-places')) || 2
        }

        // Get original prices from data attributes
        const priceBgn = priceElement.getAttribute('data-price-bgn')
        const priceEur = priceElement.getAttribute('data-price-eur')
        const priceUsd = priceElement.getAttribute('data-price-usd')

        // For USD currency, show only USD price
        if (currency === 'USD' && priceUsd) {
            const formattedPrice = this.formatCurrency(parseFloat(priceUsd), 'USD', locale, decimalPlaces)
            priceElement.textContent = formattedPrice
            return true
        }

        // For BGN/EUR currencies, use time slot logic with original prices
        if ((currency === 'BGN' || currency === 'EUR') && priceBgn && priceEur) {
            const formattedPrice = this.formatPriceWithOriginalPrices(parseFloat(priceBgn), parseFloat(priceEur), {
                locale,
                decimalPlaces
            })

            if (formattedPrice.showDual) {
                priceElement.innerHTML = this.createDualCurrencyText(formattedPrice)
            } else {
                priceElement.textContent = formattedPrice.primary.symbol
            }
            return true
        }

        // Fallback: try to use data-price attribute if original prices are not available
        const fallbackPrice = priceElement.getAttribute('data-price')
        if (fallbackPrice) {
            return this.updateElement(priceElement, parseFloat(fallbackPrice), { decimalPlaces })
        }

        return false
    }

    /**
     * Update price element with monthly/annual prices using original prices from data attributes
     * @param {Element} priceElement - Price element to update
     * @param {string} period - 'monthly' or 'annually'
     * @returns {boolean} Whether the element was updated
     */
    updateElementWithPeriodPrices(priceElement, period = 'monthly') {
        if (!priceElement) return false

        const locale = this.getMetaTag('locale')
        const currency = this.getMetaTag('currency')
        let decimalPlaces = 2

        if (priceElement.hasAttribute('data-decimal-places')) {
            decimalPlaces = parseInt(priceElement.getAttribute('data-decimal-places')) || 2
        }

        // Get original prices from data attributes based on period
        const prefix = period === 'annually' ? 'annually' : 'monthly'
        const priceBgn = priceElement.getAttribute(`data-price-${prefix}-bgn`)
        const priceEur = priceElement.getAttribute(`data-price-${prefix}-eur`)
        const priceUsd = priceElement.getAttribute(`data-price-${prefix}-usd`)

        // For USD currency, show only USD price
        if (currency === 'USD' && priceUsd) {
            const formattedPrice = this.formatCurrency(parseFloat(priceUsd), 'USD', locale, decimalPlaces)
            priceElement.textContent = formattedPrice
            return true
        }

        // For BGN/EUR currencies, use time slot logic with original prices
        if ((currency === 'BGN' || currency === 'EUR') && priceBgn && priceEur) {
            const formattedPrice = this.formatPriceWithOriginalPrices(parseFloat(priceBgn), parseFloat(priceEur), {
                locale,
                decimalPlaces
            })

            if (formattedPrice.showDual) {
                priceElement.innerHTML = this.createDualCurrencyText(formattedPrice)
            } else {
                priceElement.textContent = formattedPrice.primary.symbol
            }
            return true
        }

        // Fallback: try to use data-price-monthly or data-price-annually
        const fallbackPrice = priceElement.getAttribute(`data-price-${prefix}`)
        if (fallbackPrice) {
            return this.updateElement(priceElement, parseFloat(fallbackPrice), { decimalPlaces })
        }

        return false
    }

    /**
     * Update price element with dual currency support
     * @param {Element} priceElement - Price element to update
     * @param {number} price - Original price in BGN
     * @param {object} options - Additional options
     * @param {number} options.decimalPlaces - Number of decimal places to show (default: 2)
     * @returns {boolean} Whether the element was updated
     */
    updateElement(priceElement, price, options = {}) {
        if (!priceElement || price === null || price === undefined) return false

        const locale = this.getMetaTag('locale')
        let decimalPlaces = options.decimalPlaces || 2

        if (priceElement.hasAttribute('data-decimal-places')) {
            decimalPlaces = parseInt(priceElement.getAttribute('data-decimal-places')) || 2
        }

        let formattedPrice
        if (this.shouldShowDualCurrency()) {
            formattedPrice = this.formatPrice(price, { locale, decimalPlaces })
        } else {
            formattedPrice = {
                showDual: false,
                primary: {
                    symbol: this.formatCurrency(price, this.getMetaTag('currency'), locale, decimalPlaces)
                }
            }
        }

        if (formattedPrice.showDual) {
            priceElement.innerHTML = this.createDualCurrencyText(formattedPrice)
        } else {
            priceElement.textContent = formattedPrice.primary.symbol
        }

        return true
    }
}

// Export for use in other modules
export default PriceHandler
window.PriceHandler = PriceHandler
